<template lang="pug">
transition(name="suggestions-fade")
  .suggestions-bar(v-if="opened && cards.length")
    .iq-ico-suggested-response-grey.suggestions-icon(:title="$t('conversation_tab.chat_panel.suggestions.title')")
    .suggestions-list.scroll
      button.suggestion-card(v-for="card in cards",
        :key="card.suggestion.id",
        type="button",
        :class="{ active: card.suggestion.id === selected, document: card.isDocument }",
        :title="card.tooltip",
        @click="select(card.suggestion)")
        span.suggestion-meta
          span.suggestion-tag {{ card.label }}
          span.suggestion-source(v-if="card.source") {{ card.source }}
          //- An internal document's answer is never sent with its link.
          span.suggestion-internal(v-if="card.isInternal") {{ $t('conversation_tab.chat_panel.suggestions.internal') }}
        span.suggestion-text {{ card.text }}
</template>

<script>
import get from 'lodash/get';
import { isPublicDocument } from '@/libs/currentSuggestions';
import { htmlToPlain } from '@/libs/suggestionGhost';

const REPLY_COUNT = 3;
const DOCUMENT_COUNT = 2;

const isQuickResponse = suggestion => get(suggestion, 'metadata.type') === 'quick_response';
const isDocument = suggestion => get(suggestion, 'metadata.type') === 'document';

/*
 * Suggestions V2: reply suggestions as cards below the composer. Quick responses and IQ Tools responses
 * come first, then knowledge base answers.
 */
export default {
  name: 'suggestionsBar',
  props: {
    list: {
      type: Array,
      required: true,
    },
    selected: {
      type: Number,
    },
    opened: {
      type: Boolean,
      default: () => true,
    },
    // Fills a quick response's placeholders, as inserting it does.
    format: {
      type: Function,
      default: value => value,
    },
  },
  emits: ['select'],
  computed: {
    cards() {
      const cards = this.list.map(this.toCard).filter(card => card.text);
      return [
        ...cards.filter(card => !card.isDocument).slice(0, REPLY_COUNT),
        ...cards.filter(card => card.isDocument).slice(0, DOCUMENT_COUNT),
      ];
    },
  },
  methods: {
    toCard(suggestion) {
      // Only quick responses get their placeholders filled, as when they are inserted.
      const value = isQuickResponse(suggestion) ? this.format(suggestion.value) : suggestion.value;
      // Shown as text, never as HTML: quick response markup and customer data can't run in the dashboard.
      const text = htmlToPlain(value).replace(/\s+/g, ' ').trim();
      const label = this.label(suggestion);
      const source = this.source(suggestion);
      return {
        suggestion,
        text,
        label,
        source,
        isDocument: isDocument(suggestion),
        isInternal: isDocument(suggestion) && !isPublicDocument(suggestion),
        tooltip: `${label}${source ? ` · ${source}` : ''}\n${text}`,
      };
    },
    // Suggestions from the older AI-ENGINE path carry no type: they are IQ Tools responses.
    label(suggestion) {
      if (isDocument(suggestion)) {
        return this.$t('conversation_tab.chat_panel.suggestions.document');
      }
      return isQuickResponse(suggestion)
        ? this.$t('conversation_tab.chat_panel.suggestions.quick_response')
        : this.$t('conversation_tab.chat_panel.suggestions.iq_tools');
    },
    // The quick response's title, or the document's name.
    source(suggestion) {
      if (isDocument(suggestion)) {
        return get(suggestion, 'metadata.source_name') || '';
      }
      return isQuickResponse(suggestion) ? get(suggestion, 'metadata.title') || '' : '';
    },
    select(suggestion) {
      this.$emit('select', suggestion);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../../styles/aiq-variables.scss";

// Darker than the bar's blue, for readable labels on white.
$suggestion-accent: #3d6bc9;
// Documents get their own colour, apart from replies.
$suggestion-document-accent: #1f7a6f;

.suggestions-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 76px;
  margin: 4px 16px 0;
  padding: 0 8px;
  border-radius: 6px;
  background-color: $aig-bgc-suggested-response-box;
  box-sizing: border-box;
}
.suggestions-icon {
  flex-shrink: 0;
}
.suggestions-list {
  display: flex;
  flex: 1;
  gap: 8px;
  min-width: 0;
  overflow-x: auto;

  &.scroll {
    scrollbar-width: thin;
  }
}
// One card per suggestion: its source, then its text.
.suggestion-card {
  --accent: #{$suggestion-accent};
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 200px;
  max-width: 360px;
  height: 64px;
  padding: 6px 12px 6px 13px;
  color: $aig-text-suggested-response-box;
  font: inherit;
  text-align: left;
  border: 1px solid #c8d5ee;
  border-radius: 6px;
  background-color: #fff;
  box-shadow: inset 3px 0 0 var(--accent);
  box-sizing: border-box;
  cursor: pointer;

  &.document {
    --accent: #{$suggestion-document-accent};
  }
  &:hover,
  &:focus-visible {
    border-color: var(--accent);
    box-shadow: inset 3px 0 0 var(--accent), 0 1px 4px rgba(50, 64, 87, .18);
    outline: none;
  }
  &.active {
    border-color: var(--accent);
    background-color: #f2f6fe;
  }
}
.suggestion-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 11px;
  line-height: 14px;
  color: #6b7280;
}
.suggestion-tag {
  flex-shrink: 0;
  font-weight: 700;
  letter-spacing: .03em;
  text-transform: uppercase;
  color: var(--accent);
}
.suggestion-source {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.suggestion-internal {
  flex-shrink: 0;
  padding: 0 5px;
  font-weight: 600;
  color: #4b5563;
  border-radius: 3px;
  background-color: #eceff3;
}
.suggestion-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  font-size: 13px;
  line-height: 17px;
}
.suggestions-fade-enter-active,
.suggestions-fade-leave-active {
  transition: opacity .15s;
}
.suggestions-fade-enter-from,
.suggestions-fade-leave-to {
  opacity: 0;
}
</style>
